import React, { useRef, useState, useCallback, useEffect } from 'react';

export default function CameraModal({ onCapture, onClose , shape = 'circle'  }) {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rawImgRef = useRef(null);
  const [photoShape, setPhotoShape] = useState(shape);
  const [phase,    setPhase]   = useState('starting'); // starting|live|adjust|preview|error
  const [zoom,     setZoom]    = useState(1);
  const [adjZoom,  setAdjZoom] = useState(1);
  const [offset,   setOffset]  = useState({ x:0, y:0 });
  const [preview,  setPreview] = useState(null);
  const [error,    setError]   = useState('');
  const [facingMode, setFacingMode] = useState('environment'); // back camera default
  const dragRef = useRef({ active:false, startX:0, startY:0, ox:0, oy:0 });

  // ── Exact required output / frame dimensions ──────────────────────────────
  const CIRCLE_SIZE   = 661;             // circle: 661 x 661
  const SQUARE_W       = 661;             // square: 661 x 756
  const SQUARE_H       = 756;

  // Current frame dims based on selected shape (used for drag bounds, preview box, adjust box)
  const frameW = photoShape === 'circle' ? CIRCLE_SIZE : SQUARE_W;
  const frameH = photoShape === 'circle' ? CIRCLE_SIZE : SQUARE_H;

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Wire stream to video element
  useEffect(() => {
    if (phase === 'live' && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [phase]);

  // ── Start camera immediately on mount ──────────────────────────────────────
  const startCamera = useCallback(async (facing) => {
    setError('');
    setPhase('starting');
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facing || facingMode },
          width:  { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setPhase('live');
    } catch (e) {
      // Fallback: try without facing mode constraint
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        streamRef.current = stream;
        setPhase('live');
      } catch (e2) {
        setError('Camera error: ' + (e2.message || 'Permission denied'));
        setPhase('error');
      }
    }
  }, [facingMode, stopStream]);

  // Start immediately on mount with back camera
  useEffect(() => {
    startCamera('environment');
    return () => stopStream();
  }, []); // eslint-disable-line

  // Flip camera
  const flipCamera = useCallback(async () => {
    const next = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(next);
    await startCamera(next);
  }, [facingMode, startCamera]);

  // Capture — crop from the live video at the correct aspect ratio for the chosen shape,
  // then render onto a canvas at the EXACT target pixel dimensions.
  const handleCapture = useCallback(() => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return;

    const targetW = photoShape === 'circle' ? CIRCLE_SIZE : SQUARE_W;
    const targetH = photoShape === 'circle' ? CIRCLE_SIZE : SQUARE_H;
    const targetAspect = targetW / targetH;

    c.width = targetW;
    c.height = targetH;
    const ctx = c.getContext('2d');

    const vw = v.videoWidth, vh = v.videoHeight;
    const videoAspect = vw / vh;

    // Crop the largest centered region of the video matching targetAspect, then apply zoom
    let cropW, cropH;
    if (videoAspect > targetAspect) {
      cropH = vh;
      cropW = vh * targetAspect;
    } else {
      cropW = vw;
      cropH = vw / targetAspect;
    }
    cropW /= zoom;
    cropH /= zoom;

    const srcX = (vw - cropW) / 2;
    const srcY = (vh - cropH) / 2;

    ctx.drawImage(v, srcX, srcY, cropW, cropH, 0, 0, targetW, targetH);
    rawImgRef.current = c.toDataURL('image/jpeg', 0.95);
    stopStream();
    setOffset({ x:0, y:0 });
    setAdjZoom(1);
    setPhase('adjust');
  }, [zoom, stopStream, photoShape]);

  // Commit adjust → preview, output at EXACT target pixel dimensions for the shape
  const commitAdjust = useCallback(() => {
    const img = new Image();
    img.onload = () => {
      const c = canvasRef.current;
      const W = photoShape === 'circle' ? CIRCLE_SIZE : SQUARE_W;
      const H = photoShape === 'circle' ? CIRCLE_SIZE : SQUARE_H;
      c.width = W; c.height = H;
      const ctx = c.getContext('2d');
      ctx.clearRect(0, 0, W, H);
      ctx.save();

      if (photoShape === 'circle') {
        // Circle uses the single dimension since W === H here
        ctx.beginPath();
        ctx.arc(W / 2, H / 2, Math.min(W, H) / 2, 0, Math.PI * 2);
        ctx.clip();
      } else {
        // square (rectangle 661x756): rounded corners to look clean
        ctx.beginPath();
        ctx.roundRect(0, 0, W, H, 24);
        ctx.clip();
      }

      // img is already drawn at the right aspect from handleCapture,
      // so scale it to fill W x H exactly, then apply adjZoom + offset
      const scaledW = W * adjZoom;
      const scaledH = H * adjZoom;
      ctx.drawImage(
        img,
        (W - scaledW) / 2 + offset.x,
        (H - scaledH) / 2 + offset.y,
        scaledW, scaledH
      );
      ctx.restore();
      setPreview(c.toDataURL('image/jpeg', 0.92));
      setPhase('preview');
    };
    img.src = rawImgRef.current;
  }, [adjZoom, offset, photoShape]);

  // Drag — bounds now respect frameW / frameH independently (needed for rectangle shape)
  const onDragStart = useCallback(e => {
    e.preventDefault();
    const cx = e.touches?.[0]?.clientX ?? e.clientX;
    const cy = e.touches?.[0]?.clientY ?? e.clientY;
    dragRef.current = { active:true, startX:cx, startY:cy, ox:offset.x, oy:offset.y };
  }, [offset]);
  const onDragMove = useCallback(e => {
    if (!dragRef.current.active) return;
    e.preventDefault();
    const cx = e.touches?.[0]?.clientX ?? e.clientX;
    const cy = e.touches?.[0]?.clientY ?? e.clientY;
    const maxShiftX = (frameW * adjZoom - frameW) / 2 + 40;
    const maxShiftY = (frameH * adjZoom - frameH) / 2 + 40;
    setOffset({
      x: Math.max(-maxShiftX, Math.min(maxShiftX, dragRef.current.ox + cx - dragRef.current.startX)),
      y: Math.max(-maxShiftY, Math.min(maxShiftY, dragRef.current.oy + cy - dragRef.current.startY)),
    });
  }, [adjZoom, frameW, frameH]);
  const onDragEnd = useCallback(() => { dragRef.current.active = false; }, []);

  // const handleConfirm = useCallback(async () => {
  //   if (!preview) return;
  //   const blob = await fetch(preview).then(r => r.blob());
  //   onCapture(
  //     new File([blob], `cam_${Date.now()}.jpg`, { type: 'image/jpeg' }),
  //     photoShape  // ← this is what doUploadPhoto(file, shape) expects
  //   );
  // }, [preview, onCapture, photoShape]);
const handleConfirm = useCallback(async () => {
  if (!preview) return;

  // 1. Load the image from the preview URL
  const img = new Image();
  img.src = preview;
  await new Promise((resolve) => { img.onload = resolve; });

  // 2. Create a canvas with the image's natural size
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext('2d');

  // 3. Clear the canvas – this makes it fully transparent
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // 4. If the shape is 'circle', apply a circular clip mask
  if (photoShape === 'circle') {
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(canvas.width, canvas.height) / 2;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
  }
  // (You can add other shapes here: e.g., 'rounded', 'square', etc.)

  // 5. Draw the image – it will be clipped to the circle
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  // 6. Export as PNG (transparency preserved)
  const pngBlob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/png');
  });

  const pngFile = new File(
    [pngBlob],
    `cam_${Date.now()}.png`,
    { type: 'image/png' }
  );

  // 7. Pass the PNG file to the upload handler
  onCapture(pngFile, photoShape);
}, [preview, onCapture, photoShape]);


  const handleRetake = useCallback(() => {
    setPreview(null); setZoom(1); setOffset({ x:0, y:0 }); setAdjZoom(1);
    rawImgRef.current = null;
    startCamera(facingMode);
  }, [facingMode, startCamera]);

  const handleClose = useCallback(() => { stopStream(); onClose(); }, [stopStream, onClose]);

  // Live-view frame: scale the exact target dimensions down to fit the modal,
  // while keeping the same aspect ratio so what you frame matches the captured crop.
  const MAX_LIVE_W = 320; // max on-screen width for the live preview box
  const liveScale = Math.min(1, MAX_LIVE_W / frameW);
  const liveBoxW = Math.round(frameW * liveScale);
  const liveBoxH = Math.round(frameH * liveScale);

  // Adjust/preview boxes are also shown scaled down on screen (canvas output stays exact)
  const MAX_BOX_W = 280;
  const boxScale = Math.min(1, MAX_BOX_W / frameW);
  const boxW = Math.round(frameW * boxScale);
  const boxH = Math.round(frameH * boxScale);

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="camera-modal" onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <h2>📷 Take Photo</h2>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            {(phase==='live') && (
              <button className="btn btn-ghost btn-sm" onClick={flipCamera} title="Flip camera">🔄</button>
            )}
            <button className="modal-close" onClick={handleClose}>✕</button>
          </div>
        </div>

        <div style={{padding:'20px',textAlign:'center'}}>
          {error && (
            <div style={{background:'var(--error-bg,rgba(239,68,68,.1))',border:'1px solid var(--error,#ef4444)',borderRadius:'var(--radius-sm)',padding:'10px 14px',color:'var(--error,#ef4444)',fontSize:'.82rem',marginBottom:14}}>
              ⚠️ {error}
              <button className="btn btn-outline btn-sm" style={{marginLeft:12}} onClick={()=>startCamera(facingMode)}>
                Retry
              </button>
            </div>
          )}

          {/* STARTING */}
          {phase==='starting' && (
            <div style={{padding:40,color:'var(--text-muted)',fontSize:'.88rem'}}>
              <div className="spinner" style={{margin:'0 auto 12px'}}/>
              Starting camera…
            </div>
          )}

          {/* LIVE */}
          {phase==='live' && (
            <>
                 <div style={{display:'flex',gap:8,justifyContent:'center',marginTop:10, marginBottom:10 }}>
                  <button
                    className={`btn btn-sm ${photoShape==='circle'?'btn-primary':'btn-outline'}`}
                    onClick={()=>setPhotoShape('circle')}>
                    ⭕ Circle
                  </button>
                  <button
                    className={`btn btn-sm ${photoShape==='square'?'btn-primary':'btn-outline'}`}
                    onClick={()=>setPhotoShape('square')}>
                    ⬜ Square
                  </button>
                </div>
              <div className="camera-circle-outer"
                style={{
                  width: liveBoxW,
                  height: liveBoxH,
                  margin: '0 auto',
                  borderRadius: photoShape==='circle' ? '50%' : '12px',
                  overflow: 'hidden',
                }}>
                <div style={{width:'100%',height:'100%',transform:`scale(${zoom})`,transformOrigin:'center center'}}>
                  <video ref={videoRef} autoPlay playsInline muted
                    style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
                </div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:10,margin:'14px 8px 0',padding:'0 4px'}}>
                <span style={{fontSize:'.85rem'}}>🔍</span>
                <input type="range" min="1" max="3" step="0.05" value={zoom}
                  onChange={e=>setZoom(parseFloat(e.target.value))}
                  style={{flex:1,accentColor:'var(--primary)',cursor:'pointer',height:4}}/>
                <span style={{fontFamily:'monospace',fontSize:'.8rem',color:'var(--primary)',fontWeight:700,minWidth:36}}>{zoom.toFixed(1)}×</span>
              </div>
              <div style={{display:'flex',gap:10,justifyContent:'center',marginTop:16,flexWrap:'wrap'}}>
                
                <button className="btn btn-primary" onClick={handleCapture}>📸 Capture</button>
                <button className="btn btn-ghost btn-sm" onClick={flipCamera}>🔄 Flip</button>
                <button className="btn btn-outline" onClick={handleClose}>Cancel</button>
              </div>
            </>
          )}

          {/* ADJUST */}
          {phase==='adjust' && rawImgRef.current && (
            <>
              <p style={{color:'var(--text-muted)',fontSize:'.8rem',marginBottom:10}}>Drag to reposition · Zoom to fit</p>
              <div style={{
                  width: boxW, height: boxH,
                  borderRadius: photoShape==='circle' ? '50%' : '16px',
                  overflow:'hidden',
                  border:'3px solid var(--primary)',
                  boxShadow:'0 0 0 6px rgba(var(--primary-rgb),.15)',
                  margin:'0 auto',
                  cursor:'grab',
                  userSelect:'none',
                  touchAction:'none',
                  position:'relative',
                  background:'var(--bg3)'
                }}
                onMouseDown={onDragStart} onMouseMove={onDragMove} onMouseUp={onDragEnd} onMouseLeave={onDragEnd}
                onTouchStart={onDragStart} onTouchMove={onDragMove} onTouchEnd={onDragEnd}>
                <img src={rawImgRef.current} alt="adjust" draggable={false}
                  style={{
                    position:'absolute',
                    width: boxW*adjZoom, height: boxH*adjZoom,
                    left:'50%', top:'50%',
                    transform:`translate(calc(-50% + ${offset.x * boxScale}px), calc(-50% + ${offset.y * boxScale}px))`,
                    pointerEvents:'none'
                  }}/>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:10,margin:'12px 8px 0'}}>
                <span style={{fontSize:'.85rem'}}>🔍</span>
                <input type="range" min="0.8" max="3" step="0.05" value={adjZoom}
                  onChange={e=>{setAdjZoom(parseFloat(e.target.value));setOffset({x:0,y:0});}}
                  style={{flex:1,accentColor:'var(--primary)',cursor:'pointer',height:4}}/>
                <span style={{fontFamily:'monospace',fontSize:'.8rem',color:'var(--primary)',fontWeight:700,minWidth:36}}>{adjZoom.toFixed(1)}×</span>
              </div>
              <div style={{display:'flex',gap:10,justifyContent:'center',marginTop:14}}>
                <button className="btn btn-primary" onClick={commitAdjust}>✅ Looks Good</button>
                <button className="btn btn-outline" onClick={handleRetake}>🔄 Retake</button>
              </div>
            </>
          )}

          {/* PREVIEW */}
          {phase==='preview' && preview && (
            <>
              <img src={preview} alt="preview"
                style={{
                  width: boxW, height: boxH,
                  borderRadius: photoShape === 'circle' ? '50%' : '16px',
                  objectFit: 'cover',
                  border: '3px solid var(--success)',
                  boxShadow: '0 0 0 6px rgba(34,197,94,.15)',
                  margin: '0 auto', display: 'block'
                }}/>
              <p style={{color:'var(--text-muted)',fontSize:'.82rem',margin:'12px 0 16px'}}>
                Confirm or re-adjust · Output: {photoShape==='circle' ? `${CIRCLE_SIZE}×${CIRCLE_SIZE}px` : `${SQUARE_W}×${SQUARE_H}px`}
              </p>
              <div style={{display:'flex',gap:10,justifyContent:'center'}}>
                <button className="btn btn-success" onClick={handleConfirm}>✅ Use This Photo</button>
                <button className="btn btn-outline" onClick={()=>setPhase('adjust')}>✏️ Re-adjust</button>
                <button className="btn btn-ghost" onClick={handleRetake}>🔄 Retake</button>
              </div>
            </>
          )}
        </div>
        <canvas ref={canvasRef} style={{display:'none'}}/>
      </div>
    </div>
  );
}
